import { Button, ButtonGroup, DropdownMenu, DropdownMenuCheckIcon } from '@components'
import { useBusinessContext } from '@context'
import {
  EINVOICING_SHEET_LANGUAGES,
  EinvoicingSheetLanguage,
  fetchEinvoicingAddressSheet,
  IBusiness
} from '@query'
import copy from 'copy-to-clipboard'
import { TFunction } from 'i18next'
import React, { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Trans, useTranslation } from 'react-i18next'
import { FaCaretDown, FaCheck, FaCopy, FaDownload, FaPaperPlane } from 'react-icons/fa'
import styled from 'styled-components'
import { SendEinvoicingDetailsModal } from './SendEinvoicingDetailsModal'

const COPIED_RESET_MS = 1800

const toPdfLanguage = (code: string | undefined): EinvoicingSheetLanguage => {
  const base = (code ?? '').slice(0, 2).toLowerCase()
  return EINVOICING_SHEET_LANGUAGES.includes(base as EinvoicingSheetLanguage)
    ? (base as EinvoicingSheetLanguage)
    : 'fi'
}

const buildShareText = (business: IBusiness, translate: TFunction): string => {
  const businessId =
    business.identifiers?.find(identifier => identifier.type === 'y_tunnus')?.value ??
    business.business_id
  const lines = [
    businessId ? `${business.name} (${businessId})` : business.name,
    `${translate('settings.einvoicing.share.textAddress')}: ${business.einvoicing_address ?? ''}`,
    `${translate('settings.einvoicing.share.textOperatorCode')}: ${
      business.einvoicing_operator ?? ''
    }`
  ]
  if (business.einvoicing_operator_name) {
    lines.push(
      `${translate('settings.einvoicing.share.textOperator')}: ${business.einvoicing_operator_name}`
    )
  }
  return lines.join('\n')
}

interface Props {
  business: IBusiness
  /** Settings places the actions beside the text. The invoicing modal stacks them. */
  layout?: 'row' | 'stacked'
}

export const EInvoicingShareCard: React.FC<Props> = ({ business, layout = 'row' }) => {
  const { t, i18n } = useTranslation()
  const { slug } = useBusinessContext()
  const [pdfLang, setPdfLang] = useState<EinvoicingSheetLanguage>(() =>
    toPdfLanguage(i18n.language)
  )
  const [isDownloading, setIsDownloading] = useState(false)
  const [isSendOpen, setIsSendOpen] = useState(false)
  // Bumped on open so the modal remounts with a fresh form and the current language.
  const [sendFormKey, setSendFormKey] = useState(0)
  const [isCopied, setIsCopied] = useState(false)
  const copiedTimeout = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(copiedTimeout.current), [])

  const downloadPdf = async () => {
    setIsDownloading(true)
    try {
      await fetchEinvoicingAddressSheet({ slug, language: pdfLang })
    } catch {
      toast.error(t('settings.einvoicing.share.downloadError'))
    } finally {
      setIsDownloading(false)
    }
  }

  const copyDetails = async () => {
    await i18n.loadLanguages(pdfLang)
    if (!copy(buildShareText(business, i18n.getFixedT(pdfLang)))) return
    setIsCopied(true)
    window.clearTimeout(copiedTimeout.current)
    copiedTimeout.current = window.setTimeout(() => setIsCopied(false), COPIED_RESET_MS)
  }

  return (
    <Card $stacked={layout === 'stacked'}>
      <Text $stacked={layout === 'stacked'}>
        <Title>{t('settings.einvoicing.share.title')}</Title>
        <Body>
          <Trans i18nKey="settings.einvoicing.share.description">
            <GuideLink href={t('link.einvoicing')} target="_blank" rel="noreferrer" />
          </Trans>
        </Body>
      </Text>
      <Actions>
        <ButtonGroup>
          <Button
            type="button"
            intent="primary"
            size="sm"
            icon={<FaDownload />}
            showSpinner={isDownloading}
            onClick={() => void downloadPdf()}
          >
            {t('settings.einvoicing.share.downloadPdf')}
          </Button>
          <DropdownMenu
            items={EINVOICING_SHEET_LANGUAGES.map(code => (
              <Button
                key={code}
                type="button"
                icon={<DropdownMenuCheckIcon selected={pdfLang === code} />}
                onClick={() => setPdfLang(code)}
              >
                {t(`settings.einvoicing.share.lang.${code}`)}
              </Button>
            ))}
          >
            <Button
              type="button"
              intent="primary"
              size="sm"
              iconRight={<FaCaretDown />}
              aria-label={t('settings.einvoicing.share.lang.' + pdfLang)}
            >
              {pdfLang.toUpperCase()}
            </Button>
          </DropdownMenu>
        </ButtonGroup>
        <Button
          type="button"
          isSecondary
          size="sm"
          icon={isCopied ? <FaCheck /> : <FaCopy />}
          onClick={() => void copyDetails()}
        >
          {t(isCopied ? 'settings.einvoicing.share.copied' : 'settings.einvoicing.share.copy')}
        </Button>
        <Button
          type="button"
          isSecondary
          size="sm"
          icon={<FaPaperPlane />}
          onClick={() => {
            setSendFormKey(key => key + 1)
            setIsSendOpen(true)
          }}
        >
          {t('settings.einvoicing.share.send.button')}
        </Button>
      </Actions>
      <SendEinvoicingDetailsModal
        key={sendFormKey}
        isVisible={isSendOpen}
        language={pdfLang}
        onClose={() => setIsSendOpen(false)}
      />
    </Card>
  )
}

/** Same surface as ImageMessageBox without fillBackground. */
const Card = styled.section<{ $stacked: boolean }>`
  display: flex;
  flex-direction: ${({ $stacked }) => ($stacked ? 'column' : 'row')};
  flex-wrap: wrap;
  align-items: ${({ $stacked }) => ($stacked ? 'stretch' : 'center')};
  gap: ${({ theme, $stacked }) => ($stacked ? theme.spacing.sm : theme.spacing.md)}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid #e4e8ec;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.colors.defaultHover};
`

const Text = styled.div<{ $stacked: boolean }>`
  flex: ${({ $stacked }) => ($stacked ? 'none' : '1 1 18rem')};
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const Title = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.menuBg};
`

const Body = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.metalGray};
  text-wrap: pretty;
`

const GuideLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
